import {makeAutoObservable} from "mobx";
import type {MithraMaterializedApi, Ordering} from "../../../../services/MithraMaterializedApi";
import type {PpvPageController} from "./PpvPageController";
import type {
    BasePpvFilter,
    PpvAllLevelsFilter,
    PpvFilter,
    PpvGroupFilter,
    PpvItemListFilter
} from "../classes/PpvApiClasses";
import {extractOptionFromInclExclFilter} from "../classes/PpvApiClasses";
import {catsToDict} from "../../../../services/ApiHelpers";
import {SearchManager} from "../../../../stores/managers/SearchManager";
import type {BusinessUnit} from "../../../../services/classes/MaterializedClasses";
import type {PpvGroupRowState} from "../classes/PpvGroupRowState";
import type {SelectionResult} from "../../../../components/visualization/api-dropdown-filter/ApiDropdownTypes";
import {MultiSelection} from "../../../../components/visualization/api-dropdown-filter/ApiDropdownTypes";
import {
    API_DROPDOWN_FILTER_DEFAULT_SELECT_ALL
} from "../../../../components/visualization/api-dropdown-filter/ApiDropdownFilterComponent";

/**
 * All method related to navigating the categorization review
 */
export class PpvPageFilterController {
    selectedCategory: string[] = [];
    ppvSearchManager = new SearchManager('ppv', () => this.onSearch());

    ordering?: Ordering;

    /**
     * The business unit that is selected,
     * null if only the missing business units are selected, (Not implemented)
     * undefined if all business units are selected, e.g. there is no filter
     * TODO: Dead code, remove
     * TODO: Test this with the 'open'-opportunity if we still want to develop that feature further...
     */
    businessUnitFilterField: BusinessUnit | undefined
    businessUnitFilterOptions: BusinessUnit[] | undefined

    isFilterDialogOpen = false; // TODO: Remove this dialog

    startDate: string | undefined = undefined;
    endDate: string | undefined = undefined;

    supplierFilter: SelectionResult | null = null;
    ccDescriptionFilter: SelectionResult | null = null;
    pNameFilter: SelectionResult | null = null;

    businessUnitFilter: SelectionResult | null = null;
    // TODO: Add businessUnitFilter (with additional NULL value)

    // noinspection JSUnusedLocalSymbols
    constructor(
        private ppvPageController: PpvPageController,
        private api: MithraMaterializedApi,
    ) {
        makeAutoObservable(this)
    }

    get hasActiveFilter(): boolean {
        return this.baseFilter.databag !== -1 && (
            Boolean(this.startDate)
            || Boolean(this.endDate)
            || Boolean(this.businessUnitFilterField)
            // || (this.selectedCategory.length ?? 0) > 0 TODO: L1234 filter is not used in the BE filter request
            || MultiSelection.isActive(this.supplierFilter)
            || MultiSelection.isActive(this.businessUnitFilter)
            || MultiSelection.isActive(this.ccDescriptionFilter)
            || MultiSelection.isActive(this.pNameFilter)
        )
    }

    /**
     * Convert the filter from the state variable to the API URL params
     */
    get baseFilter(): BasePpvFilter {
        const requestedBagId = this.ppvPageController.bagId;
        if (!requestedBagId) {
            console.warn('ppvPageController: requestedBagId is not properly refactored', requestedBagId)
        }
        // noinspection UnnecessaryLocalVariableJS
        const filter: BasePpvFilter = {
            databag: requestedBagId || -1,
            business_unit: this.businessUnitFilterField?.id,
            quarter_date__gte: this.startDate,
            quarter_date__lte: this.endDate,
        };
        return filter;
    }

    get searchFilter(): BasePpvFilter {
        const filter: BasePpvFilter = {
            ...this.baseFilter,
        };
        if (this.supplierFilter && this.supplierFilter.selected.length > 0) {
            const objectIds = this.supplierFilter.selected.map(option => Number(option.id));
            if (this.supplierFilter.inclusive) {
                // See BasePpvFilter for explanation
                filter.supplier__in = objectIds.join(',');
            } else {
                filter.supplier__not = objectIds;
            }
        }
        if (this.businessUnitFilter && this.businessUnitFilter.selected.length > 0) {
            const objectIds = this.businessUnitFilter.selected.map(option => Number(option.id));
            if (this.businessUnitFilter.inclusive) {
                // See BasePpvFilter for explanation
                filter.business_unit__in = objectIds.join(',');
            } else {
                filter.business_unit__not = objectIds;
            }
        }
        if (this.ccDescriptionFilter && this.ccDescriptionFilter.selected.length > 0) {
            this.ccDescriptionFilter.selected.forEach(option => {
                if (String(option.id).includes(',')) {
                    console.error('ccDescriptionFilter: id contains a comma this is incompatible with DRF Filter implementation', option)
                }
            })
            const joined = this.ccDescriptionFilter.selected.map(o => String(o.id));
            if (this.ccDescriptionFilter.inclusive) {
                // See BasePpvFilter for explanation
                filter.cc_description__in = joined.join(',');
            } else {
                filter.cc_description__not = joined;
            }
        }
        if (this.pNameFilter && this.pNameFilter.selected.length > 0) {
            this.pNameFilter.selected.forEach(option => {
                if (String(option.id).includes(',')) {
                    console.error('pNameFilter: id contains a comma this is incompatible with DRF Filter implementation', option)
                }
            })
            const joined = this.pNameFilter.selected.map(s => String(s.id));
            if (this.pNameFilter.inclusive) {
                // See BasePpvFilter for explanation
                filter.p_name__in = joined.join(',');
            } else {
                filter.p_name__not = joined;
            }
        }
        return filter;
    }

    get activeFilter(): BasePpvFilter {
        //FIXME: deprecated
        // Idem to statisticsFilter
        return {...this.searchFilter};
    }

    get levelAndSearchFilter(): PpvFilter {
        const lCats = catsToDict(this.selectedCategory, this.selectedLevel);
        // noinspection UnnecessaryLocalVariableJS
        const filter: PpvFilter = {
            ...this.searchFilter,
            ...lCats,
            level: this.selectedLevel,
        };
        return filter;
    }

    get statisticsFilter(): PpvAllLevelsFilter {
        const f = {...this.searchFilter};
        f.exclude_disqualified = true;
        return f;
    }

    get selectedLevel(): number {
        return this.selectedCategory.length;
    }

    get hasRemainingSelectionLevels(): boolean {
        if (this.selectedLevel >= this.ppvPageController.taxonomySize) {
            return false
        }
        // noinspection RedundantIfStatementJS
        if (this.selectedLevel >= 1 && !this.selectedCategoryLabel) {
            // Do not allow to go deeper in uncategorized
            return false;
        }
        return true;
    }

    get canSelectLevelDeeper(): boolean {
        return this.hasRemainingSelectionLevels;
    }

    get selectedCategoryLabel(): string | undefined {
        if (this.selectedCategory.length > 0) {
            return this.selectedCategory[this.selectedCategory.length - 1]
        }
        return undefined;
    }

    setBusinessUnitFilterField(value: BusinessUnit | undefined) {
        this.businessUnitFilterField = value;
        this.ppvPageController.dataFilterChanged();
    }

    selectNextCategoryDown(selectedCategory: string) {
        this.setSelectedCategory([...this.selectedCategory, selectedCategory])
    }

    navigateToLevel(level: number) {
        this.setSelectedCategory(this.selectedCategory.slice(0, level));
    }

    setSelectedCategory(selectedCategory: string[]) {
        const obj = {old: `${this.selectedCategory}`, new: `${selectedCategory}`};
        console.debug('CategorizationReviewPageFilterController.setSelectedCategory', obj);

        this.selectedCategory = selectedCategory;

        this.ppvPageController.resetAndRequestSupplierPage();
        // Note: Do not update the statistics
    }

    setFilterDialogOpen(open: boolean) {
        this.isFilterDialogOpen = open;
    }

    onSearch() {
        this.ppvPageController.loadData()
    }

    getBUs() {
        this.api.listBusinessUnitData(this.ppvPageController.bagId).then(r => {
            this.businessUnitFilterOptions = r.data
        })
    }

    dateFilterChanged(startDate: string | undefined, endDate: string | undefined) {
        this.startDate = startDate;
        this.endDate = endDate;
        this.ppvPageController.dataFilterChanged();
    }

    setSupplierFilter(selection: SelectionResult) {
        this.supplierFilter = selection;
        this.ppvPageController.dataFilterChanged();
    }

    setBusinessUnitFilter(selection: SelectionResult) {
        this.businessUnitFilter = selection;
        this.ppvPageController.dataFilterChanged();
    }

    setCcDescriptionFilter(selection: SelectionResult) {
        this.ppvPageController.dataFilterChanged();
    }

    setPNameFilter(selection: SelectionResult) {
        this.pNameFilter = selection;
        this.ppvPageController.dataFilterChanged();
    }

    /**
     * The state of the filter that we send to the API
     * TODO: The PpvGroupFilter class is not up to date with the latest expected fields in the API
     */
    get ppvGroupFilter(): PpvGroupFilter {
        //FIXME Used te be called selectedFilter

        // Search at the Group level (only)
        let search: string | undefined = undefined;

        // For the grouped mode the search is set in the filter as the getPartUrlSearchParams() is not used
        if (this.ppvSearchManager.activeSearchString) {
            search = this.ppvSearchManager.activeSearchString
        }

        // noinspection UnnecessaryLocalVariableJS
        const filter: PpvGroupFilter = {
            ...this.levelAndSearchFilter,
            search: search,
            page: this.ppvPageController.ppvPageDataController.ppvGroupPages.page,
            page_size: this.ppvPageController.ppvPageDataController.ppvGroupPages.pageSize,
        };
        return filter;
    }

    get selectedFilter(): PpvGroupFilter {
        //FIXME: Deprecated
        return {...this.ppvGroupFilter};
    }

    selectedItemFilter(group: PpvGroupRowState): PpvItemListFilter {
        // noinspection UnnecessaryLocalVariableJS
        const filter: PpvItemListFilter = {
            ...this.levelAndSearchFilter,
            p_name: group.data.p_name,
            no_page: true,
        };
        return filter;
    }

    setActiveGroupFilter(filter: PpvGroupFilter) {
        this.startDate = filter.quarter_date__gte;
        this.endDate = filter.quarter_date__lte;
        this.businessUnitFilterField = this.businessUnitFilterOptions?.find(bu => bu.id === filter.business_unit);
        this.ppvSearchManager.activeSearchString = filter.search || '';
        this.ppvSearchManager.setSearch(filter.search || '');
        this.ppvSearchManager.doSearch('auto-reset');
        this.setSelectedCategory([
            filter.l1,
            filter.l2,
            filter.l3,
            filter.l4,
            filter.l5,
            filter.l6,
            filter.l7,
            filter.l8,
        ].filter(Boolean));

        const pNameFilter = extractOptionFromInclExclFilter(filter, 'p_name');
        this.pNameFilter = {
            inclusive: pNameFilter?.inclusive ?? API_DROPDOWN_FILTER_DEFAULT_SELECT_ALL,
            selected: pNameFilter?.selected || [],
        }

        const supplierFilter = extractOptionFromInclExclFilter(filter, 'supplier_id');
        this.supplierFilter = {
            inclusive: supplierFilter?.inclusive ?? API_DROPDOWN_FILTER_DEFAULT_SELECT_ALL,
            selected: supplierFilter?.selected || [],
        }

        const ccDescriptionFilter = extractOptionFromInclExclFilter(filter, 'cc_description');
        this.ccDescriptionFilter = {
            inclusive: ccDescriptionFilter?.inclusive ?? API_DROPDOWN_FILTER_DEFAULT_SELECT_ALL,
            selected: ccDescriptionFilter?.selected || [],
        }

        const businessUnitFilter = extractOptionFromInclExclFilter(filter, 'business_unit_id');
        this.businessUnitFilter = {
            inclusive: businessUnitFilter?.inclusive ?? API_DROPDOWN_FILTER_DEFAULT_SELECT_ALL,
            selected: businessUnitFilter?.selected || [],
        }
    }
}
